import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import WikiTocTree, { TreeNode } from './WikiTocTree';
import {
    WikiTOC,
    WikiArticleSummary,
    articlesIn,
    findSectionPath,
} from '../utils/wikiToc';

/**
 * A section's own articles come before its sub-sections, so a reader scanning
 * down the rail meets pages before deeper groups.
 */
export const buildNavNodes = (
    section: WikiTOC,
    articles: WikiArticleSummary[]
): TreeNode[] => [
    ...articlesIn(articles, section.id).map(article => ({
        id: article.id,
        label: article.title,
        kind: 'article' as const,
        href: `/wiki/article/${article.id}`,
    })),
    ...(section.children || []).map(child => ({
        id: child.id,
        label: child.description,
        kind: 'section' as const,
        href: `/wiki/section/${child.id}`,
        children: buildNavNodes(child, articles),
    })),
];

interface WikiDocsNavProps {
    /** The top-level section being browsed — the rail shows its whole subtree. */
    section: WikiTOC;
    articles: WikiArticleSummary[];
    /** Article or section id of the current page. */
    activeId?: string | null;
    /** The section the current page sits in, used to open the path to it. */
    activeSectionId?: string | null;
}

const WikiDocsNav: React.FC<WikiDocsNavProps> = ({
    section,
    articles,
    activeId,
    activeSectionId,
}) => {
    const nodes = buildNavNodes(section, articles);

    // Open every section between the root and the current page.
    const expandIds = findSectionPath([section], activeSectionId || activeId).map(s => s.id);

    return (
        <aside className="hidden lg:block w-72 flex-shrink-0 no-print">
            <div className="sticky top-8">
                <Link
                    to="/wiki"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand-600 mb-3"
                >
                    <ArrowLeft className="w-4 h-4" />
                    All documentation
                </Link>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <Link
                            to={`/wiki/section/${section.id}`}
                            className="text-sm font-semibold text-gray-800 hover:text-brand-600 break-words"
                        >
                            {section.description}
                        </Link>
                    </div>

                    {nodes.length === 0 ? (
                        <p className="text-sm text-gray-400 italic px-4 py-4">Nothing in this section yet.</p>
                    ) : (
                        <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                            <WikiTocTree
                                nodes={nodes}
                                selectedId={activeId}
                                defaultExpanded={false}
                                expandIds={expandIds}
                            />
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default WikiDocsNav;
